import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { X } from 'lucide-react'
import { toast } from 'react-toastify'
import { adminAPI, agentsAPI, phoneNumbersAPI, appsAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'

function AssignResourcesModal({ isOpen, onClose, user }) {
  const [selectedAgents, setSelectedAgents] = useState([])
  const [selectedPhones, setSelectedPhones] = useState([])
  const [selectedApps, setSelectedApps] = useState([])
  const queryClient = useQueryClient()

  const { data: agents } = useQuery('agents', agentsAPI.getAgents, { enabled: isOpen })
  const { data: phoneNumbers } = useQuery('phone-numbers', phoneNumbersAPI.getPhoneNumbers, { enabled: isOpen })
  const { data: apps } = useQuery('apps', appsAPI.getApps, { enabled: isOpen })

  const assignMutation = useMutation(adminAPI.assignResources, {
    onSuccess: () => {
      toast.success('Resources assigned successfully')
      queryClient.invalidateQueries('users')
      queryClient.invalidateQueries('agents')
      queryClient.invalidateQueries('phone-numbers')
      queryClient.invalidateQueries('apps')
      onClose()
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to assign resources')
    }
  })

  useEffect(() => {
    if (user && isOpen) {
      setSelectedAgents(user.assignedAgents || [])
      setSelectedPhones(user.assignedPhoneNumbers || [])
      setSelectedApps(user.assignedApps || [])
    }
  }, [user, isOpen])

  const handleAssign = () => {
    if (!user) return

    assignMutation.mutate({
      userId: user._id,
      agentIds: selectedAgents,
      phoneNumberIds: selectedPhones,
      appIds: selectedApps
    })
  }

  const toggleResource = (resourceId, selectedList, setSelectedList) => {
    setSelectedList(prev => 
      prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    )
  }

  if (!user) return null

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-primary-400 hover:text-primary-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-primary-900">
                      Assign Resources to {user.firstName} {user.lastName}
                    </Dialog.Title>

                    <div className="mt-6 space-y-8">
                      {/* Agents Section */}
                      <div>
                        <h4 className="text-md font-medium text-primary-900 mb-4">AI Agents</h4>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {agents?.data?.agents?.map((agent) => (
                            <div key={agent._id} className="relative">
                              <label className="flex items-center p-3 border border-primary-200 rounded-lg cursor-pointer hover:bg-primary-50">
                                <input
                                  type="checkbox"
                                  checked={selectedAgents.includes(agent._id)}
                                  onChange={() => toggleResource(agent._id, selectedAgents, setSelectedAgents)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                                />
                                <div className="ml-3 min-w-0 flex-1">
                                  <p className="text-sm font-medium text-primary-900 truncate">
                                    {agent.name}
                                  </p>
                                  <p className="text-sm text-primary-500 truncate">
                                    {agent.voiceName || 'No voice assigned'}
                                  </p>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                        {!agents?.data?.agents?.length && (
                          <p className="text-sm text-primary-500">No agents available</p>
                        )}
                      </div>

                      {/* Phone Numbers Section */}
                      <div>
                        <h4 className="text-md font-medium text-primary-900 mb-4">Phone Numbers</h4>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {phoneNumbers?.data?.phoneNumbers?.map((phone) => (
                            <div key={phone._id} className="relative">
                              <label className="flex items-center p-3 border border-primary-200 rounded-lg cursor-pointer hover:bg-primary-50">
                                <input
                                  type="checkbox"
                                  checked={selectedPhones.includes(phone._id)}
                                  onChange={() => toggleResource(phone._id, selectedPhones, setSelectedPhones)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                                />
                                <div className="ml-3 min-w-0 flex-1">
                                  <p className="text-sm font-medium text-primary-900 truncate">
                                    {phone.phoneNumber}
                                  </p>
                                  <p className="text-sm text-primary-500 truncate">
                                    {phone.carrierName || 'Unknown carrier'}
                                  </p>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                        {!phoneNumbers?.data?.phoneNumbers?.length && (
                          <p className="text-sm text-primary-500">No phone numbers available</p>
                        )}
                      </div>

                      {/* Apps Section */}
                      <div>
                        <h4 className="text-md font-medium text-primary-900 mb-4">Applications</h4>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {apps?.data?.apps?.map((app) => (
                            <div key={app._id} className="relative">
                              <label className="flex items-center p-3 border border-primary-200 rounded-lg cursor-pointer hover:bg-primary-50">
                                <input
                                  type="checkbox"
                                  checked={selectedApps.includes(app._id)}
                                  onChange={() => toggleResource(app._id, selectedApps, setSelectedApps)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                                />
                                <div className="ml-3 min-w-0 flex-1">
                                  <p className="text-sm font-medium text-primary-900 truncate">
                                    {app.name}
                                  </p>
                                  <p className="text-sm text-primary-500 truncate">
                                    {app.speechSynthesisVendor || 'No TTS vendor'}
                                  </p>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                        {!apps?.data?.apps?.length && (
                          <p className="text-sm text-primary-500">No applications available</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-8 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAssign}
                        disabled={assignMutation.isLoading}
                        className="btn-primary"
                      >
                        {assignMutation.isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                        {assignMutation.isLoading ? 'Assigning...' : 'Assign Resources'}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default AssignResourcesModal